import type Point from 'ol/geom/Point';

import { ajax } from 'cadenza/api-client/api';
import type { Authority } from 'cadenza/api-client/srs-api';
import type { GeocodingConfigDto } from 'cadenza/geocoding/dto/geocoding-config-dto';
import type { ReverseGeocodedLocationDto } from 'cadenza/geocoding/dto/reverse-geocoded-location-dto';

/**
 * Find address at the specified location (reverse geo-coding).
 *
 * @param points - location to be reverse-geocoded
 * @param authority - coordinate system
 * @return a single address in structured and formatted form, with distance to specified location
 */
export function findAddresses(points: Point, authority: Authority): Promise<ReverseGeocodedLocationDto | null>;

/**
 * Find address at the specified location (reverse geo-coding).
 *
 * @param points - locations to be reverse-geocoded
 * @param authority - coordinate system
 * @return addresses in structured and formatted form, with distance to specified location, in the same order as the specified points
 */
export function findAddresses(points: Point[], authority: Authority): Promise<(ReverseGeocodedLocationDto | null)[]>;

/**
 * Find address at the specified location (reverse geo-coding).
 *
 * @param points - location(s) to be reverse-geocoded, a single point or an array of points
 * @param authority - coordinate system
 * @return addresses (a single one or an array depending on the input) in structured and formatted form, with distance to specified location
 */
export function findAddresses(
  points: Point | Point[],
  authority: Authority,
): Promise<ReverseGeocodedLocationDto | null | (ReverseGeocodedLocationDto | null)[]> {
  let singlePoint = false;
  if (!Array.isArray(points)) {
    points = [points];
    singlePoint = true;
  }
  const params = new URLSearchParams();
  params.set('points', JSON.stringify(toMultiPoint(points)));
  params.set('authority', authority);
  return ajax<ReverseGeocodedLocationDto[]>(`/reverse-geocoding?${params.toString()}`).then((locations) => {
    if (singlePoint) {
      return locations[0];
    } else {
      return locations;
    }
  });
}

/**
 * Returns configuration for geocoding operation.
 * This contains information what types are available (structured address and
 * unstructured address), limit and default name mapping for
 * preselected attributes/columns.
 *
 * @return a promise containing a json object with config properties
 * @see net/disy/cadenza/workbook/web/operation/geocoding/rest/OperationConfigEndpointController.java
 */
export function getGeocodingConfiguration() {
  return ajax<GeocodingConfigDto>('/operation/config/geocoding', {
    type: 'GET',
  });
}

function toMultiPoint(points: Point[]) {
  return {
    type: 'MultiPoint',
    coordinates: points.map((point) => point.getCoordinates()),
  };
}
